import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Download, TrendingUp, Users, Calendar, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MonthlyData {
  month: string;
  required_meetings: number;
  completed_meetings: number;
  compliance_rate: number;
  active_assignments: number;
}

interface ComplianceBreakdown {
  status: string;
  count: number;
  percentage: number;
}

interface MentorPerformance {
  mentor_name: string;
  total_mentees: number;
  total_meetings: number;
  avg_compliance: number;
}

export function MentorProgramReporting() {
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [complianceBreakdown, setComplianceBreakdown] = useState<ComplianceBreakdown[]>([]);
  const [mentorPerformance, setMentorPerformance] = useState<MentorPerformance[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(true);

  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280'];

  useEffect(() => {
    loadReportingData();
  }, [selectedYear]);

  const loadReportingData = async () => {
    try {
      await Promise.all([
        loadMonthlyData(),
        loadComplianceBreakdown(),
        loadMentorPerformance()
      ]);
    } catch (error) {
      console.error('Error loading reporting data:', error);
      toast.error('Failed to load reporting data');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyData = async () => {
    try {
      const monthlyStats: MonthlyData[] = [];
      
      for (let month = 1; month <= 12; month++) {
        const startDate = new Date(parseInt(selectedYear), month - 1, 1);
        const endDate = new Date(parseInt(selectedYear), month, 0);
        endDate.setHours(23, 59, 59, 999);

        // Get active assignments for this month
        const { data: assignments, error: assignmentsError } = await supabase
          .from('mentor_assignments')
          .select('mentor_id, mentee_id')
          .eq('academic_year', selectedYear)
          .eq('status', 'active')
          .lte('assignment_start_date', endDate.toISOString().split('T')[0])
          .or(`assignment_end_date.is.null,assignment_end_date.gte.${startDate.toISOString().split('T')[0]}`);

        if (assignmentsError) throw assignmentsError;

        const activeAssignments = assignments?.length || 0;
        const requiredMeetings = activeAssignments * 2; // 2 meetings per assignment per month

        // Get completed meetings for this month
        const { data: meetings, error: meetingsError } = await supabase
          .from('meetings')
          .select('id')
          .eq('meeting_type', 'mentoring')
          .eq('status', 'completed')
          .gte('scheduled_date', startDate.toISOString())
          .lte('scheduled_date', endDate.toISOString());

        if (meetingsError) throw meetingsError;

        const completedMeetings = meetings?.length || 0;
        const complianceRate = requiredMeetings > 0 
          ? Math.round((completedMeetings / requiredMeetings) * 100) 
          : 0;

        monthlyStats.push({
          month: startDate.toLocaleDateString('en-US', { month: 'short' }),
          required_meetings: requiredMeetings,
          completed_meetings: completedMeetings,
          compliance_rate: Math.min(complianceRate, 100),
          active_assignments: activeAssignments
        });
      }

      setMonthlyData(monthlyStats);
    } catch (error) {
      console.error('Error loading monthly data:', error);
    }
  };

  const loadComplianceBreakdown = async () => {
    try {
      // Get current month's compliance data
      const currentDate = new Date();
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const { data: assignments, error } = await supabase
        .from('mentor_assignments')
        .select('mentor_id, mentee_id')
        .eq('academic_year', selectedYear)
        .eq('status', 'active');

      if (error) throw error;

      if (!assignments || assignments.length === 0) {
        setComplianceBreakdown([]);
        return;
      }

      const complianceStats = { compliant: 0, at_risk: 0, non_compliant: 0, no_meetings: 0 };

      for (const assignment of assignments) {
        const { data: meetings } = await supabase
          .from('meetings')
          .select('id, status')
          .eq('observer_id', assignment.mentor_id)
          .eq('observee_id', assignment.mentee_id)
          .eq('meeting_type', 'mentoring')
          .gte('scheduled_date', startOfMonth.toISOString())
          .lte('scheduled_date', endOfMonth.toISOString());

        const completedMeetings = meetings?.filter(m => m.status === 'completed').length || 0;
        const requiredMeetings = 2; // Assume 2 meetings per month

        if (completedMeetings === 0) {
          complianceStats.no_meetings++;
        } else if (completedMeetings >= requiredMeetings) {
          complianceStats.compliant++;
        } else if (completedMeetings >= 1) {
          complianceStats.at_risk++;
        } else {
          complianceStats.non_compliant++;
        }
      }

      const total = assignments.length;
      const breakdown: ComplianceBreakdown[] = [
        { status: 'Compliant', count: complianceStats.compliant, percentage: Math.round((complianceStats.compliant / total) * 100) },
        { status: 'At Risk', count: complianceStats.at_risk, percentage: Math.round((complianceStats.at_risk / total) * 100) },
        { status: 'Non-Compliant', count: complianceStats.non_compliant, percentage: Math.round((complianceStats.non_compliant / total) * 100) },
        { status: 'No Meetings', count: complianceStats.no_meetings, percentage: Math.round((complianceStats.no_meetings / total) * 100) }
      ];

      setComplianceBreakdown(breakdown);
    } catch (error) {
      console.error('Error loading compliance breakdown:', error);
    }
  };

  const loadMentorPerformance = async () => {
    try {
      const { data: assignments, error } = await supabase
        .from('mentor_assignments')
        .select('mentor_id, mentee_id')
        .eq('academic_year', selectedYear)
        .eq('status', 'active');

      if (error) throw error;

      if (!assignments || assignments.length === 0) {
        setMentorPerformance([]);
        return;
      }

      // Group by mentor
      const mentorStats = new Map();

      for (const assignment of assignments) {
        const mentorId = assignment.mentor_id;
        
        // Get mentor profile
        const { data: mentorProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', mentorId)
          .single();

        const mentorName = mentorProfile ? `${mentorProfile.first_name} ${mentorProfile.last_name}` : 'Unknown Mentor';

        if (!mentorStats.has(mentorId)) {
          mentorStats.set(mentorId, {
            mentor_name: mentorName,
            mentee_ids: [],
            total_meetings: 0,
            total_required: 0
          });
        }

        const stats = mentorStats.get(mentorId);
        stats.mentee_ids.push(assignment.mentee_id);

        // Get meetings for this mentor-mentee pair
        const { data: meetings } = await supabase
          .from('meetings')
          .select('id, status')
          .eq('observer_id', mentorId)
          .eq('observee_id', assignment.mentee_id)
          .eq('meeting_type', 'mentoring')
          .gte('scheduled_date', `${selectedYear}-01-01`)
          .lt('scheduled_date', `${parseInt(selectedYear) + 1}-01-01`);

        const completedMeetings = meetings?.filter(m => m.status === 'completed').length || 0;
        stats.total_meetings += completedMeetings;
        
        // Calculate required meetings (assume 2 per month for active period)
        const currentMonth = new Date().getMonth() + 1;
        stats.total_required += currentMonth * 2;
      }

      const performanceData: MentorPerformance[] = Array.from(mentorStats.values()).map(stats => ({
        mentor_name: stats.mentor_name,
        total_mentees: stats.mentee_ids.length,
        total_meetings: stats.total_meetings,
        avg_compliance: stats.total_required > 0 
          ? Math.round((stats.total_meetings / stats.total_required) * 100)
          : 0
      })).sort((a, b) => b.avg_compliance - a.avg_compliance);

      setMentorPerformance(performanceData);
    } catch (error) {
      console.error('Error loading mentor performance:', error);
    }
  };

  const generateComprehensiveReport = async () => {
    try {
      // Generate a comprehensive report in CSV format
      const reportData = {
        summary: {
          year: selectedYear,
          total_assignments: mentorPerformance.reduce((sum, m) => sum + m.total_mentees, 0),
          total_mentors: mentorPerformance.length,
          total_meetings: mentorPerformance.reduce((sum, m) => sum + m.total_meetings, 0),
          overall_compliance: monthlyData.length > 0 
            ? Math.round(monthlyData.reduce((sum, m) => sum + m.compliance_rate, 0) / monthlyData.length)
            : 0
        },
        monthly_data: monthlyData,
        compliance_breakdown: complianceBreakdown,
        mentor_performance: mentorPerformance
      };

      // Create CSV content
      let csvContent = `NEW TEACHER MENTOR PROGRAM REPORT - ${selectedYear}\n\n`;
      
      csvContent += `SUMMARY\n`;
      csvContent += `Total Assignments,${reportData.summary.total_assignments}\n`;
      csvContent += `Total Mentors,${reportData.summary.total_mentors}\n`;
      csvContent += `Total Meetings,${reportData.summary.total_meetings}\n`;
      csvContent += `Overall Compliance,${reportData.summary.overall_compliance}%\n\n`;

      csvContent += `MONTHLY COMPLIANCE TRACKING\n`;
      csvContent += `Month,Required Meetings,Completed Meetings,Compliance Rate,Active Assignments\n`;
      monthlyData.forEach(month => {
        csvContent += `${month.month},${month.required_meetings},${month.completed_meetings},${month.compliance_rate}%,${month.active_assignments}\n`;
      });

      csvContent += `\nCOMPLIANCE BREAKDOWN\n`;
      csvContent += `Status,Count,Percentage\n`;
      complianceBreakdown.forEach(breakdown => {
        csvContent += `${breakdown.status},${breakdown.count},${breakdown.percentage}%\n`;
      });

      csvContent += `\nMENTOR PERFORMANCE\n`;
      csvContent += `Mentor Name,Total Mentees,Total Meetings,Average Compliance\n`;
      mentorPerformance.forEach(mentor => {
        csvContent += `${mentor.mentor_name},${mentor.total_mentees},${mentor.total_meetings},${mentor.avg_compliance}%\n`;
      });

      // Download the report
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `mentor-program-report-${selectedYear}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Comprehensive report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };

  const years = [
    (new Date().getFullYear() - 2).toString(),
    (new Date().getFullYear() - 1).toString(),
    new Date().getFullYear().toString(),
    (new Date().getFullYear() + 1).toString()
  ];

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading reporting data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Mentor Program Analytics</h2>
          <p className="text-muted-foreground">
            Comprehensive reporting and analytics for desegregation compliance
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={generateComprehensiveReport} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export Full Report
          </Button>
        </div>
      </div>

      {/* Monthly Compliance Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Monthly Compliance Tracking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'compliance_rate' ? `${value}%` : value,
                  name === 'compliance_rate' ? 'Compliance Rate' : 
                  name === 'completed_meetings' ? 'Completed Meetings' : 'Required Meetings'
                ]}
              />
              <Bar dataKey="required_meetings" fill="#e5e7eb" name="required_meetings" />
              <Bar dataKey="completed_meetings" fill="#10b981" name="completed_meetings" />
              <Line 
                type="monotone" 
                dataKey="compliance_rate" 
                stroke="#3b82f6" 
                strokeWidth={3}
                name="compliance_rate"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Current Compliance Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={complianceBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                  >
                    {complianceBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2">
                {complianceBreakdown.map((item, index) => (
                  <div key={item.status} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm">
                      {item.status}: {item.count} ({item.percentage}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mentor Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Top Performing Mentors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mentorPerformance.slice(0, 5).map((mentor, index) => (
                <div key={mentor.mentor_name} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{mentor.mentor_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {mentor.total_mentees} mentees • {mentor.total_meetings} meetings
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={mentor.avg_compliance >= 80 ? "default" : 
                                mentor.avg_compliance >= 60 ? "secondary" : "destructive"}
                    >
                      {mentor.avg_compliance}%
                    </Badge>
                  </div>
                </div>
              ))}
              {mentorPerformance.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  No mentor performance data available
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}